import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ref as dbRef, get, set, remove, onValue } from 'firebase/database';
import { database } from '../config/firebase';
import { useSEO } from '../marketing/hooks.jsx';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Search, MapPin, Star, X, Car, Locate, Loader2,
  Map as MapIcon, MessageSquare, ChevronDown, ChevronUp, Quote, ArrowUpRight,
  Calendar, SlidersHorizontal, LayoutGrid, List,
  ExternalLink, Heart, CheckCircle2, Shield,
} from 'lucide-react';
import { geocodeAddress, distanceKm } from '../utils/geocode';
import { usePublicReviews } from '../hooks/useReviews';
import ReviewStars from '../components/ReviewStars';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { parseLocalDate, toLocalISODate } from '../utils/helpers';
import {
  CITY_MIN_COMPANIES, MODE_PATH, buildCityIndex, slugifyCity, humanizeSlug,
} from '../utils/cityPages';

// Fuori dal componente: array stabile, non ricreato ad ogni render
// (altrimenti AddressAutocomplete rifarebbe la callback di ricerca ad ogni giro).
const GEOCODE_TYPES = ['geocode'];

// Custom moto icon
const MotoIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5.5" cy="17.5" r="3.5" /><circle cx="18.5" cy="17.5" r="3.5" />
    <path d="M15 6l-2 4H8.5L7 13.5" /><path d="M15 6h3l1.5 4H15" />
    <path d="M8.5 10L6 17.5" /><path d="M15 10l1.5 4" />
  </svg>
);

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const BRAND = '#2563EB';
const BRAND_LIGHT = 'rgba(37,99,235,0.08)';
const ITALY_CENTER = [42.5, 12.5];

function RecenterMap({ center, zoom }) {
  const map = useMap();
  const lat = center[0];
  const lng = center[1];
  useEffect(() => {
    // 'center' è volutamente escluso: il genitore passa spesso un nuovo array
    // [lat, lng] identico nei valori ad ogni render, e includerlo qui
    // farebbe ri-centrare la mappa continuamente. Si traccia solo lat/lng.
    map.setView([lat, lng], zoom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng, zoom, map]);
  return null;
}

const formatReviewDate = (iso) => {
  try { return new Date(iso).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return ''; }
};

// ── Oggi / domani come default per le date ──────────────────────────────────
const todayISO = () => toLocalISODate();

// ── Formatta data leggibile ─────────────────────────────────────────────────
const formatDateLabel = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso + 'T12:00:00').toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  } catch { return iso; }
};

// ── Calcola giorni tra due date ─────────────────────────────────────────────
const daysBetween = (from, to) => {
  if (!from || !to) return null;
  const d1 = new Date(from), d2 = new Date(to);
  const diff = Math.round((d2 - d1) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : null;
};

// ── Barra di ricerca stile booking ─────────────────────────────────────────
const SearchBar = ({
  query, setQuery,
  addressQuery, setAddressQuery, addressCoords, setAddressCoords,
  geocoding, geocodeError,
  geolocating,
  dateFrom, setDateFrom, dateTo, setDateTo,
  vehicleFilter, setVehicleFilter, hideVehicleTabs,
  handleAddressSearch, handleUseMyLocation, handlePlaceSelected,
  onSearch,
}) => {
  const nights = daysBetween(dateFrom, dateTo);

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden">
      {/* Tab tipo veicolo (nascosti sulle pagine città: la pagina è già di un tipo) */}
      {!hideVehicleTabs && (
      <div className="flex border-b border-slate-100">
        {[
          { key: 'tutti', label: 'Tutti i veicoli', icon: null },
          { key: 'auto', label: 'Auto', icon: <Car className="w-4 h-4" /> },
          { key: 'moto', label: 'Moto & Scooter', icon: <MotoIcon className="w-4 h-4" /> },
        ].map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setVehicleFilter(opt.key)}
            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1.5 text-xs sm:text-sm font-bold px-4 sm:px-6 py-3.5 border-b-2 transition-all"
            style={vehicleFilter === opt.key
              ? { borderColor: BRAND, color: BRAND, background: BRAND_LIGHT }
              : { borderColor: 'transparent', color: '#94a3b8' }}
          >
            {opt.icon}{opt.label}
          </button>
        ))}
      </div>
      )}

      {/* Campi di ricerca */}
      <div className="p-4 sm:p-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">

          {/* Dove */}
          <div className="lg:col-span-1">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Dove</label>
            <form onSubmit={handleAddressSearch} className="relative">
              <AddressAutocomplete
                value={addressQuery}
                onChange={val => { setAddressQuery(val); setAddressCoords(null); }}
                onPlaceSelected={handlePlaceSelected}
                placeholder="Città o indirizzo..."
                types={GEOCODE_TYPES}
                showHint={false}
                className="w-full pl-9 pr-9 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15 transition"
              />
              {addressQuery && (
                <button type="button" onClick={() => { setAddressCoords(null); setAddressQuery(''); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500 z-10">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </form>
            <button type="button" onClick={handleUseMyLocation} disabled={geolocating}
              className="mt-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 hover:text-slate-600 transition disabled:opacity-50">
              {geolocating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Locate className="w-3 h-3" />}
              Usa la mia posizione
            </button>
            {addressCoords && !geolocating && (
              <p className="text-[11px] mt-1 flex items-center gap-1" style={{ color: BRAND }}>
                <MapPin className="w-3 h-3" /> Posizione trovata
              </p>
            )}
            {geocoding && <p className="text-[11px] text-slate-400 mt-1">Ricerca indirizzo...</p>}
            {geocodeError && <p className="text-[11px] text-red-500 mt-1">{geocodeError}</p>}
          </div>

          {/* Nome o città */}
          <div className="lg:col-span-1">
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">Nome azienda</label>
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="es. Rossi Autonoleggio..."
                className="w-full pl-9 pr-4 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15 transition"
              />
            </div>
          </div>

          {/* Data inizio */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Ritiro</span>
            </label>
            <input
              type="date"
              value={dateFrom}
              min={todayISO()}
              onChange={e => {
                setDateFrom(e.target.value);
                // se la data di fine è prima della nuova data inizio, aggiusta
                if (dateTo && e.target.value >= dateTo) {
                  const next = parseLocalDate(e.target.value);
                  next.setDate(next.getDate() + 1);
                  setDateTo(toLocalISODate(next));
                }
              }}
              className="w-full px-3.5 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15 transition"
            />
          </div>

          {/* Data fine */}
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 block">
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Restituzione</span>
            </label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || todayISO()}
              onChange={e => setDateTo(e.target.value)}
              className="w-full px-3.5 py-3 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15 transition"
            />
          </div>
        </div>

        {/* Bottone cerca */}
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={onSearch}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all"
          >
            <Search className="w-4 h-4" />
            {nights ? `Cerca disponibilità · ${nights} ${nights === 1 ? 'giorno' : 'giorni'}` : 'Cerca noleggi'}
          </button>
          {nights && (
            <span className="text-sm text-slate-400 font-medium">
              {formatDateLabel(dateFrom)} → {formatDateLabel(dateTo)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Card singolo noleggio ──────────────────────────────────────────────────
const CompanyCard = ({ company, isFavorite, onToggleFavorite, dateFrom, dateTo, viewMode, isLoggedIn }) => {
  const { reviews, avgRating, count } = usePublicReviews(company.companyId, false);
  const [expanded, setExpanded] = useState(false);
  const hasReviews = count > 0;
  const initial = (company.nome || '?').trim().charAt(0).toUpperCase();
  const isExternal = company.customDomain && company.customDomainVerified;

  // Costruisce URL con parametri date se disponibili
  const buildUrl = (base) => {
    if (!dateFrom || !dateTo) return base;
    const params = new URLSearchParams({ dal: dateFrom, al: dateTo }).toString();
    if (base.startsWith('http')) return `${base}?${params}`;
    return `${base}?${params}`;
  };

  const publicUrlWithDates = buildUrl(company.publicUrl);
  const NameLink = isExternal ? 'a' : Link;
  const nameLinkProps = isExternal
    ? { href: publicUrlWithDates, target: '_blank', rel: 'noopener noreferrer' }
    : { to: publicUrlWithDates };

  if (viewMode === 'list') {
    // Vista lista compatta
    return (
      <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 hover:border-slate-200 hover:shadow-md transition-all bg-white">
        <div className="shrink-0 w-14 h-14 rounded-xl border border-slate-100 bg-slate-50 flex items-center justify-center overflow-hidden">
          {company.logo ? (
            <img src={company.logo} alt={`Logo ${company.nome}`} loading="lazy" className="w-full h-full object-contain p-1.5" />
          ) : (
            <span className="text-xl font-black" style={{ color: BRAND }}>{initial}</span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <NameLink {...nameLinkProps} className="font-black text-slate-900 hover:underline truncate">{company.nome}</NameLink>
            {hasReviews && <ReviewStars rating={Number(avgRating)} size="xs" showValue count={count} />}
          </div>
          {(company.indirizzo || company.citta) && (
            <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3 shrink-0" />
              {company.indirizzo && company.citta ? `${company.indirizzo}, ${company.citta}` : (company.indirizzo || company.citta)}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            {company.mostraAuto && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">Auto</span>}
            {company.mostraMoto && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Moto</span>}
            {company.vehicleCount > 0 && <span className="text-[10px] text-slate-400">{company.vehicleCount} veicoli</span>}
            {company.distanza != null && <span className="text-[10px] font-bold" style={{ color: BRAND }}>{company.distanza.toFixed(1)} km</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Bottone preferiti lista */}
          <button
            onClick={() => onToggleFavorite(company)}
            title={!isLoggedIn ? 'Accedi per salvare nei preferiti' : isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
            className="p-2 rounded-full border transition"
            style={isLoggedIn && isFavorite
              ? { borderColor: '#fca5a5', background: '#fff1f2' }
              : { borderColor: '#e2e8f0', background: '#f8fafc' }}
          >
            <Heart className="w-4 h-4"
              fill={isLoggedIn && isFavorite ? '#ef4444' : 'none'}
              stroke={isLoggedIn && isFavorite ? '#ef4444' : '#94a3b8'}
            />
          </button>
          <NameLink {...nameLinkProps} className="px-5 py-3 text-sm font-bold text-white rounded-xl hover:opacity-90 transition"
            style={{ background: BRAND }}>
            Prenota {isExternal && <ExternalLink className="w-3 h-3 inline ml-1" />}
          </NameLink>
        </div>
      </div>
    );
  }

  // Vista griglia (default)
  return (
    <div className="rounded-2xl border border-slate-100 hover:border-slate-200 hover:shadow-xl transition-all overflow-hidden bg-white flex flex-col group">
      {/* Header colorato con logo/iniziale */}
      <div className="relative h-28 flex items-center justify-center overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${BRAND}12 0%, ${BRAND}08 100%)` }}>
        {company.logo ? (
          <img src={company.logo} alt={`Logo ${company.nome}`} loading="lazy"
            className="max-h-16 max-w-[140px] object-contain drop-shadow-sm" />
        ) : (
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl font-black shadow-sm border border-white/60"
            style={{ background: BRAND_LIGHT, color: BRAND }}>
            {initial}
          </div>
        )}
        {/* Bottone preferiti */}
        <button
          onClick={() => onToggleFavorite(company)}
          title={!isLoggedIn ? 'Accedi per salvare nei preferiti' : isFavorite ? 'Rimuovi dai preferiti' : 'Aggiungi ai preferiti'}
          className="absolute top-3 right-3 p-2 rounded-full bg-white/80 backdrop-blur-sm hover:bg-white shadow-sm transition"
        >
          <Heart
            className="w-4 h-4"
            fill={isLoggedIn && isFavorite ? '#ef4444' : 'none'}
            stroke={isLoggedIn && isFavorite ? '#ef4444' : '#94a3b8'}
          />
        </button>
        {/* Badge distanza */}
        {company.distanza != null && (
          <span className="absolute top-3 left-3 text-[11px] font-bold px-2 py-1 rounded-full bg-white/90 shadow-sm"
            style={{ color: BRAND }}>
            {company.distanza.toFixed(1)} km
          </span>
        )}
      </div>

      <div className="p-4 sm:p-5 flex-1 flex flex-col">
        {/* Nome e indirizzo */}
        <div className="mb-3">
          <NameLink {...nameLinkProps} className="font-black text-base text-slate-900 hover:underline leading-tight block">
            {company.nome}
          </NameLink>
          {(company.indirizzo || company.citta) && (
            <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
              <MapPin className="w-3 h-3 shrink-0" />
              <span className="truncate">
                {company.indirizzo && company.citta
                  ? `${company.indirizzo}, ${company.citta}`
                  : (company.indirizzo || company.citta)}
              </span>
            </p>
          )}
        </div>

        {/* Badge veicoli */}
        <div className="flex items-center gap-1.5 flex-wrap mb-3">
          {company.mostraAuto && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
              <Car className="w-3 h-3" /> Auto
            </span>
          )}
          {company.mostraMoto && (
            <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">
              <MotoIcon className="w-3 h-3" /> Moto
            </span>
          )}
          {company.vehicleCount > 0 && (
            <span className="text-[11px] text-slate-400 ml-auto font-medium">
              {company.vehicleCount} veicoli disponibili
            </span>
          )}
        </div>

        {/* Recensioni */}
        <div className="flex-1">
          {hasReviews ? (
            <ReviewStars rating={Number(avgRating)} size="sm" showValue count={count} />
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-slate-400">
              <Star className="w-3.5 h-3.5" /> Nessuna recensione ancora
            </span>
          )}
        </div>

        {/* CTA principale */}
        <NameLink
          {...nameLinkProps}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-bold py-3 rounded-xl text-white transition hover:opacity-90 active:scale-[0.98]"
          style={{ background: BRAND }}
        >
          {dateFrom && dateTo ? (
            <>
              <Calendar className="w-4 h-4" />
              Verifica disponibilità
            </>
          ) : (
            <>
              Vai al sito {isExternal && <ArrowUpRight className="w-4 h-4" />}
            </>
          )}
        </NameLink>

        {/* Toggle recensioni */}
        {hasReviews && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg border border-slate-100 hover:bg-slate-50 transition"
            style={{ color: BRAND }}
          >
            <MessageSquare className="w-3.5 h-3.5" />
            {expanded ? 'Nascondi' : `Leggi le ${count} recensioni`}
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>

      {/* Recensioni espanse */}
      {expanded && hasReviews && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 sm:px-5 py-4 space-y-2.5">
          {reviews.slice(0, 3).map((r) => (
            <div key={r.id} className="bg-white rounded-lg border border-slate-100 p-3">
              <div className="flex items-center justify-between gap-2 mb-1">
                <ReviewStars rating={r.rating} size="xs" />
                <span className="text-[11px] text-slate-400 shrink-0">{formatReviewDate(r.createdAt)}</span>
              </div>
              {r.titolo && <p className="text-xs font-bold text-slate-800 mb-0.5">{r.titolo}</p>}
              {r.testo && (
                <p className="text-xs text-slate-600 leading-relaxed flex gap-1">
                  <Quote className="w-3 h-3 text-slate-300 shrink-0 mt-0.5" />
                  <span className="line-clamp-3">{r.testo}</span>
                </p>
              )}
              <p className="text-[11px] text-slate-400 mt-1">{r.userName || 'Cliente'}</p>
            </div>
          ))}
          {count > 3 && (
            <NameLink
              {...nameLinkProps}
              className="block text-center text-xs font-semibold hover:underline"
              style={{ color: BRAND }}
            >
              Vedi tutte le {count} recensioni →
            </NameLink>
          )}
        </div>
      )}
    </div>
  );
};

// ── SEO copy per le tre varianti ────────────────────────────────────────────
const SITE_URL = 'https://www.noleggioincloud.app';
// ── Directory: mappatura record → oggetto azienda ─────────────────────────
// Usata sia dal caricamento live da Firebase sia dallo "snapshot" iniettato
// da scripts/prerender.js (window.__DIRECTORY_SEED__), così l'HTML statico
// contiene già l'elenco dei noleggi visibile ai crawler.
function mapDirectoryEntries(entries) {
  const results = [];
  Object.keys(entries || {}).forEach(key => {
    const d = entries[key];
    if (!d || !d.slug) return;
    const publicUrl = (d.customDomain && d.customDomainVerified)
      ? `https://${d.customDomain}` : `/${d.slug}`;
    results.push({
      companyId: key,
      slug: d.slug,
      publicUrl,
      nome: d.nome || d.slug,
      citta: d.comune || '',
      indirizzo: d.indirizzo || '',
      logo: d.logoAzienda || '',
      lat: typeof d.latitudine === 'number' ? d.latitudine : null,
      lng: typeof d.longitudine === 'number' ? d.longitudine : null,
      vehicleCount: typeof d.vehicleCount === 'number' ? d.vehicleCount : null,
      mostraAuto: d.mostraAuto !== false,
      mostraMoto: d.mostraMoto !== false,
      customDomain: d.customDomain,
      customDomainVerified: d.customDomainVerified,
    });
  });
  return results;
}

// Snapshot presente solo durante il prerender (mai nel browser degli utenti).
function readPrerenderSeed() {
  if (typeof window === 'undefined' || !window.__DIRECTORY_SEED__) return null;
  return mapDirectoryEntries(window.__DIRECTORY_SEED__);
}

const PAGE_COPY = {
  tutti: {
    title: 'Cerca noleggio auto e moto online | NoleggioinCloud',
    description: 'Trova e prenota il noleggio auto o moto più vicino a te. Confronta prezzi, recensioni e disponibilità tra tutti gli autonoleggi e motonoleggi registrati su NoleggioinCloud.',
    path: '/cerca',
    h1: 'Trova il noleggio perfetto per te',
    subtitle: 'Cerca tra gli autonoleggi e i noleggi moto vicino a te. Confronta recensioni, verifica disponibilità e prenota online in pochi click.',
    schema: {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Cerca un noleggio', item: `${SITE_URL}/cerca` },
      ],
    },
  },
  auto: {
    title: 'Noleggio auto vicino a me: prenota online | NoleggioinCloud',
    description: 'Cerca e prenota un noleggio auto vicino a te. Confronta disponibilità, prezzi e recensioni di tutti gli autonoleggi su NoleggioinCloud.',
    path: '/noleggio-auto',
    h1: 'Prenota un noleggio auto vicino a te',
    subtitle: 'Trova l\'autonoleggio più vicino, confronta le recensioni e prenota direttamente online.',
    schema: {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Noleggio auto', item: `${SITE_URL}/noleggio-auto` },
      ],
    },
  },
  moto: {
    title: 'Noleggio moto e scooter: prenota online | NoleggioinCloud',
    description: 'Cerca e prenota un noleggio moto o scooter vicino a te. Disponibilità in tempo reale e prenotazione online su NoleggioinCloud.',
    path: '/noleggio-moto',
    h1: 'Prenota un noleggio moto o scooter',
    subtitle: 'Trova il noleggio moto o scooter più vicino, confronta le recensioni e prenota subito online.',
    schema: {
      '@context': 'https://schema.org', '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Noleggio moto', item: `${SITE_URL}/noleggio-moto` },
      ],
    },
  },
};

// ── Testi e dati strutturati delle pagine per città ───────────────────────
const MODE_LABEL = {
  auto: { plural: 'autonoleggi', name: 'Noleggio auto', h1: 'Noleggio auto', what: 'auto' },
  moto: { plural: 'noleggi moto e scooter', name: 'Noleggio moto', h1: 'Noleggio moto e scooter', what: 'moto e scooter' },
};

function cityCopy(mode, cityName, count) {
  const L = MODE_LABEL[mode];
  const path = MODE_PATH[mode];
  const citySlug = slugifyCity(cityName);
  const plural = count === 1 ? (mode === 'auto' ? 'autonoleggio' : 'noleggio moto') : L.plural;
  return {
    title: `${L.h1} a ${cityName}: prenota online | NoleggioinCloud`,
    description: count > 0
      ? `Confronta ${count} ${plural} a ${cityName}: disponibilità, recensioni e prenotazione online direttamente dal sito di ogni noleggio.`
      : `Cerca e prenota ${L.what} a ${cityName} su NoleggioinCloud.`,
    path: `${path}/${citySlug}`,
    h1: `${L.h1} a ${cityName}`,
    subtitle: `Scegli tra i noleggi di ${cityName}, confronta le recensioni e prenota ${L.what} online in pochi click.`,
    parentPath: path,
    parentName: L.name,
  };
}

const absUrl = (u) => (u.startsWith('http') ? u : `${SITE_URL}${u}`);

// ── Componente principale ───────────────────────────────────────────────────
const SearchCompanies = ({ user, vehicleMode = 'tutti' }) => {
  const { citta: cittaParam } = useParams();
  const isCity = !!cittaParam && vehicleMode !== 'tutti';
  const navigate = useNavigate();
  // Durante il prerender l'elenco arriva già pronto (window.__DIRECTORY_SEED__);
  // per gli utenti veri parte vuoto e si riempie dalla lettura live qui sotto.
  const [seed] = useState(readPrerenderSeed);
  const [companies, setCompanies] = useState(seed || []);
  const [loading, setLoading] = useState(!seed);
  const [query, setQuery] = useState('');
  const [addressQuery, setAddressQuery] = useState('');
  const [addressCoords, setAddressCoords] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [geolocating, setGeolocating] = useState(false);
  const [favorites, setFavorites] = useState({});
  const [loginToast, setLoginToast] = useState(false); // toast "accedi per i preferiti"
  const [vehicleFilter, setVehicleFilter] = useState(vehicleMode);
  useEffect(() => { setVehicleFilter(vehicleMode); }, [vehicleMode]);

  // Date di ricerca
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Ordinamento
  const [sortBy, setSortBy] = useState('default'); // 'default' | 'distance' | 'rating' | 'fleet'

  // Vista
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'

  // Mappa
  const [showMap, setShowMap] = useState(false);

  // ── Pagine per città ─────────────────────────────────────────────────────
  // Tutte le città con almeno un noleggio del tipo giusto (serve per sapere se
  // quella dell'URL esiste); i link e l'indicizzazione usano invece la soglia.
  const allCities = useMemo(
    () => (vehicleMode === 'tutti' ? [] : buildCityIndex(companies, vehicleMode)),
    [companies, vehicleMode],
  );
  const linkableCities = useMemo(
    () => allCities.filter(c => c.count >= CITY_MIN_COMPANIES),
    [allCities],
  );
  const cityEntry = isCity ? allCities.find(c => c.slug === cittaParam) || null : null;
  const cityName = cityEntry ? cityEntry.name : humanizeSlug(cittaParam);
  const unknownCity = isCity && !loading && !cityEntry;
  const thinCity = isCity && !!cityEntry && cityEntry.count < CITY_MIN_COMPANIES;
  const noindex = unknownCity || thinCity;
  // Altro tipo di veicolo nella stessa città (link incrociato nell'intestazione)
  const otherMode = vehicleMode === 'auto' ? 'moto' : 'auto';
  const otherModeCity = useMemo(() => {
    if (!isCity) return null;
    const hit = buildCityIndex(companies, otherMode).find(c => c.slug === cittaParam);
    return hit && hit.count >= CITY_MIN_COMPANIES ? hit : null;
  }, [companies, isCity, otherMode, cittaParam]);

  // Base su cui lavorano filtri e ordinamento: sulle pagine città solo quella città
  const baseCompanies = useMemo(() => {
    if (!isCity) return companies;
    return companies.filter(c => (
      (vehicleMode === 'auto' ? c.mostraAuto : c.mostraMoto) && slugifyCity(c.citta) === cittaParam
    ));
  }, [companies, isCity, vehicleMode, cittaParam]);

  const copy = isCity
    ? cityCopy(vehicleMode, cityName, baseCompanies.length)
    : (PAGE_COPY[vehicleMode] || PAGE_COPY.tutti);

  // Dati strutturati: briciole di pane + elenco dei noleggi (solo nome + URL,
  // niente valutazioni inventate).
  const seoSchema = useMemo(() => {
    if (noindex) return undefined;
    const visible = baseCompanies.filter(c =>
      vehicleMode === 'auto' ? c.mostraAuto : vehicleMode === 'moto' ? c.mostraMoto : true);
    const breadcrumb = isCity
      ? {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: copy.parentName, item: `${SITE_URL}${copy.parentPath}` },
          { '@type': 'ListItem', position: 3, name: cityName, item: `${SITE_URL}${copy.path}` },
        ],
      }
      : { ...copy.schema, '@context': undefined };
    if (!visible.length) return isCity ? { '@context': 'https://schema.org', ...breadcrumb } : copy.schema;
    return {
      '@context': 'https://schema.org',
      '@graph': [
        breadcrumb,
        {
          '@type': 'ItemList',
          name: copy.h1,
          numberOfItems: visible.length,
          itemListElement: visible.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.nome,
            url: absUrl(c.publicUrl),
          })),
        },
      ],
    };
  }, [baseCompanies, copy, vehicleMode, isCity, cityName, noindex]);
  useSEO({ title: copy.title, description: copy.description, path: copy.path, schema: seoSchema, noindex });

  // Caricamento directory
  useEffect(() => {
    let cancelled = false;
    async function loadDirectory() {
      try {
        const indexSnap = await get(dbRef(database, 'directoryIndex'));
        if (!indexSnap.exists()) { if (!cancelled) setCompanies([]); return; }
        const results = mapDirectoryEntries(indexSnap.val());
        if (!cancelled) setCompanies(results);
      } catch (error) {
        console.error('Errore caricamento directory:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadDirectory();
    return () => { cancelled = true; };
  }, []);

  // Preferiti
  useEffect(() => {
    if (!user) { setFavorites({}); return; }
    const favRef = dbRef(database, `users/${user.uid}/favorites`);
    const unsubscribe = onValue(favRef, snap => setFavorites(snap.exists() ? snap.val() : {}));
    return () => unsubscribe();
  }, [user]);

  const toggleFavorite = async (company) => {
    if (!user) {
      setLoginToast(true);
      setTimeout(() => setLoginToast(false), 4000);
      return;
    }
    const favRef = dbRef(database, `users/${user.uid}/favorites/${company.companyId}`);
    try {
      if (favorites[company.companyId]) {
        await remove(favRef);
      } else {
        await set(favRef, { nome: company.nome, slug: company.slug, addedAt: new Date().toISOString() });
      }
    } catch (error) { console.error('Errore preferiti:', error); }
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) { setGeocodeError('Browser non supporta la geolocalizzazione.'); return; }
    setGeolocating(true); setGeocodeError('');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setAddressCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setAddressQuery('La tua posizione');
        setGeolocating(false);
        setSortBy('distance');
      },
      err => {
        setGeolocating(false);
        setGeocodeError(err.code === err.PERMISSION_DENIED
          ? 'Permesso negato. Abilita la geolocalizzazione nel browser.'
          : 'Impossibile rilevare la posizione.');
      },
      { timeout: 10000 }
    );
  };

  // Selezione di un suggerimento reale da Google Places: coordinate esatte,
  // niente bisogno di geocodificare — ordina subito per distanza.
  const handlePlaceSelected = ({ indirizzo, citta, lat, lng }) => {
    const label = [indirizzo, citta].filter(Boolean).join(', ');
    setAddressQuery(label || addressQuery);
    setAddressCoords({ lat, lng });
    setGeocodeError('');
    setSortBy('distance');
    // Piccolo delay per lasciar chiudere il dropdown dei suggerimenti prima
    // di scrollare, altrimenti lo scroll parte mentre il menu è ancora aperto.
    setTimeout(() => {
      document.getElementById('risultati')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const handleAddressSearch = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setGeocodeError('');
    if (!addressQuery.trim()) { setAddressCoords(null); return; }
    setGeocoding(true);
    const coords = await geocodeAddress(`${addressQuery}, Italia`);
    setGeocoding(false);
    if (!coords) { setGeocodeError('Indirizzo non trovato. Prova a essere più specifico.'); return; }
    setAddressCoords(coords);
    setSortBy('distance');
  };

  // Azione ricerca principale (bottone cerca)
  const handleSearch = () => {
    if (addressQuery.trim() && !addressCoords) {
      handleAddressSearch();
    }
    // scroll verso i risultati
    document.getElementById('risultati')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Lista filtrata e ordinata
  const filtered = useMemo(() => {
    let list = baseCompanies;
    if (vehicleFilter === 'auto') list = list.filter(c => c.mostraAuto);
    if (vehicleFilter === 'moto') list = list.filter(c => c.mostraMoto);
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(c => c.nome.toLowerCase().includes(q) || c.citta.toLowerCase().includes(q));
    }
    // Aggiunge distanza se c'è un punto di riferimento
    if (addressCoords) {
      list = list.map(c => ({
        ...c,
        distanza: (c.lat != null && c.lng != null)
          ? distanceKm(addressCoords.lat, addressCoords.lng, c.lat, c.lng)
          : null,
      }));
    }
    // Ordinamento
    list = [...list];
    if (sortBy === 'distance') {
      list.sort((a, b) => {
        if (a.distanza == null) return 1; if (b.distanza == null) return -1;
        return a.distanza - b.distanza;
      });
    } else if (sortBy === 'fleet') {
      list.sort((a, b) => (b.vehicleCount || 0) - (a.vehicleCount || 0));
    }
    // sortBy 'rating' viene gestito in render (senza le recensioni live nel filtro)
    return list;
  }, [baseCompanies, query, addressCoords, vehicleFilter, sortBy]);

  const firstWithCoords = baseCompanies.find(c => c.lat != null && c.lng != null);
  const mapCenter = addressCoords
    ? [addressCoords.lat, addressCoords.lng]
    : (firstWithCoords ? [firstWithCoords.lat, firstWithCoords.lng] : ITALY_CENTER);
  const mapZoom = addressCoords ? 11 : 6;

  // ── Trust badges ───────────────────────────────────────────────────────────
  const trustBadges = [
    { icon: <Shield className="w-4 h-4" />, label: 'Prenotazione sicura' },
    { icon: <CheckCircle2 className="w-4 h-4" />, label: 'Noleggi verificati' },
    { icon: <Star className="w-4 h-4" />, label: 'Recensioni reali' },
  ];

  return (
    <div className="min-h-screen" data-directory-ready={loading ? 'false' : 'true'} style={{ background: 'linear-gradient(180deg, #F0F2F8 0%, #E8EBF5 100%)' }}>

      {/* ── Toast "accedi per i preferiti" ───────────────────────────────── */}
      {loginToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-xl bg-slate-900 text-white text-sm font-semibold animate-fade-in"
          style={{ minWidth: 280, maxWidth: 380 }}>
          <Heart className="w-4 h-4 shrink-0 text-red-400" />
          <span>Accedi per salvare i preferiti</span>
          <button
            onClick={() => navigate('/account')}
            className="ml-auto px-3 py-1 rounded-lg text-xs font-bold bg-white text-slate-900 hover:bg-slate-100 transition shrink-0"
          >
            Accedi
          </button>
          <button onClick={() => setLoginToast(false)} className="text-white/50 hover:text-white transition">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div style={{ background: 'linear-gradient(160deg, #1E3A8A 0%, #1D4ED8 50%, #2563EB 100%)' }} className="pt-10 pb-16 px-5 sm:px-8 relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1.5px, transparent 1.5px)',
            backgroundSize: '24px 24px',
            maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 60%, transparent 100%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, #000 60%, transparent 100%)',
          }}
        />
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="text-xs text-white/50 mb-6 flex items-center gap-1.5">
            <Link to="/" className="hover:text-white/80 transition">Home</Link>
            <span>/</span>
            {isCity ? (
              <>
                <Link to={copy.parentPath} className="hover:text-white/80 transition">{copy.parentName}</Link>
                <span>/</span>
                <span className="text-white/80">{cityName}</span>
              </>
            ) : (
              <span className="text-white/80">{copy.h1}</span>
            )}
          </nav>

          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight mb-3">
              {copy.h1}
            </h1>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto">
              {copy.subtitle}
            </p>
            {/* Cross-link varianti */}
            {isCity ? (
              <div className="flex items-center justify-center gap-4 mt-4 text-sm font-semibold flex-wrap">
                <Link to={copy.parentPath} className="text-white/60 hover:text-white transition">
                  Tutte le città →
                </Link>
                {otherModeCity && (
                  <Link to={`${MODE_PATH[otherMode]}/${otherModeCity.slug}`} className="text-white/60 hover:text-white transition">
                    {otherMode === 'auto' ? 'Noleggio auto' : 'Noleggio moto'} a {otherModeCity.name} →
                  </Link>
                )}
              </div>
            ) : (
            <div className="flex items-center justify-center gap-4 mt-4 text-sm font-semibold">
              {vehicleMode !== 'auto' && (
                <Link to="/noleggio-auto" className="text-white/60 hover:text-white transition">Solo noleggio auto →</Link>
              )}
              {vehicleMode !== 'moto' && (
                <Link to="/noleggio-moto" className="text-white/60 hover:text-white transition">Solo noleggio moto →</Link>
              )}
              {vehicleMode !== 'tutti' && (
                <Link to="/cerca" className="text-white/50 hover:text-white/70 transition">Tutti i noleggi →</Link>
              )}
            </div>
            )}
          </div>

          {/* Search bar */}
          <SearchBar
            query={query} setQuery={setQuery}
            addressQuery={addressQuery} setAddressQuery={setAddressQuery}
            addressCoords={addressCoords} setAddressCoords={setAddressCoords}
            geocoding={geocoding} geocodeError={geocodeError}
            geolocating={geolocating}
            dateFrom={dateFrom} setDateFrom={setDateFrom}
            dateTo={dateTo} setDateTo={setDateTo}
            vehicleFilter={vehicleFilter} setVehicleFilter={setVehicleFilter} hideVehicleTabs={isCity}
            handleAddressSearch={handleAddressSearch}
            handleUseMyLocation={handleUseMyLocation}
            handlePlaceSelected={handlePlaceSelected}
            onSearch={handleSearch}
          />

          {/* Trust badges */}
          <div className="flex items-center justify-center gap-5 sm:gap-8 mt-6 flex-wrap">
            {trustBadges.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-white/60 text-xs font-semibold">
                {b.icon}{b.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── RISULTATI ────────────────────────────────────────────────────── */}
      <div id="risultati" className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-10">

        {/* Toolbar risultati */}
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <p className="text-sm text-slate-500">
            {loading ? (
              <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-slate-400" /> Caricamento noleggi...</span>
            ) : (
              <><span className="font-black text-slate-900 text-base">{filtered.length}</span>{' '}
                {filtered.length === 1 ? 'noleggio trovato' : 'noleggi trovati'}
                {dateFrom && dateTo && (
                  <span className="ml-2 text-slate-400">· {formatDateLabel(dateFrom)} – {formatDateLabel(dateTo)}</span>
                )}
              </>
            )}
          </p>

          <div className="flex items-center gap-2">
            {/* Ordina */}
            <div className="flex items-center gap-1.5">
              <SlidersHorizontal className="w-3.5 h-3.5 text-slate-400" />
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                className="text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-slate-300"
              >
                <option value="default">Ordine predefinito</option>
                {addressCoords && <option value="distance">Distanza</option>}
                <option value="fleet">Flotta più grande</option>
              </select>
            </div>

            {/* Vista */}
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1">
              <button type="button" onClick={() => setViewMode('grid')}
                className="p-1.5 rounded-md transition"
                style={viewMode === 'grid' ? { background: BRAND_LIGHT, color: BRAND } : { color: '#94a3b8' }}>
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => setViewMode('list')}
                className="p-1.5 rounded-md transition"
                style={viewMode === 'list' ? { background: BRAND_LIGHT, color: BRAND } : { color: '#94a3b8' }}>
                <List className="w-4 h-4" />
              </button>
            </div>

            {/* Toggle mappa */}
            <button type="button" onClick={() => setShowMap(v => !v)}
              className="inline-flex items-center gap-1.5 text-xs font-bold px-3.5 py-2 rounded-lg border transition"
              style={showMap ? { background: BRAND, color: 'white', borderColor: BRAND } : { background: 'white', color: BRAND, borderColor: '#e2e8f0' }}>
              <MapIcon className="w-3.5 h-3.5" />
              {showMap ? 'Nascondi mappa' : 'Mappa'}
            </button>
          </div>
        </div>

        <div className={`grid gap-6 ${showMap ? 'lg:grid-cols-5' : ''}`}>

          {/* Mappa */}
          {showMap && (
            <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden sticky top-4" style={{ minHeight: 480, height: 'calc(100vh - 120px)', maxHeight: 700 }}>
              <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: '100%', width: '100%' }}>
                <RecenterMap center={mapCenter} zoom={mapZoom} />
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {addressCoords && (
                  <Marker position={[addressCoords.lat, addressCoords.lng]}>
                    <Popup>La tua posizione di ricerca</Popup>
                  </Marker>
                )}
                {filtered.filter(c => c.lat != null && c.lng != null).map(c => (
                  <Marker key={c.companyId} position={[c.lat, c.lng]}>
                    <Popup>
                      <div style={{ minWidth: 150 }}>
                        <p style={{ fontWeight: 700, marginBottom: 4 }}>{c.nome}</p>
                        {c.citta && <p style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>{c.citta}</p>}
                        {c.distanza != null && <p style={{ fontSize: 11, color: BRAND, fontWeight: 700, marginBottom: 6 }}>{c.distanza.toFixed(1)} km di distanza</p>}
                        <a href={c.publicUrl} target="_blank" rel="noopener noreferrer"
                          style={{ color: BRAND, fontWeight: 700, fontSize: 13 }}>
                          Prenota ora →
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}

          {/* Lista */}
          <div className={showMap ? 'lg:col-span-2' : ''}>
            {loading ? (
              // Skeleton che ricalca la vera CompanyCard (header con logo,
              // nome, indirizzo, badge, bottone) invece di blocchi grigi
              // generici: dà subito la forma della pagina prima che i dati
              // arrivino.
              <div className={`grid gap-5 ${viewMode === 'grid' && !showMap ? 'sm:grid-cols-2 lg:grid-cols-3' : ''}`}>
                {[0, 1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="rounded-2xl border border-slate-100 bg-white overflow-hidden animate-pulse">
                    <div className="h-28 bg-slate-100" />
                    <div className="p-4">
                      <div className="h-4 w-2/3 bg-slate-100 rounded mb-2" />
                      <div className="h-3 w-1/2 bg-slate-100 rounded mb-3" />
                      <div className="flex gap-2 mb-4">
                        <div className="h-4 w-12 bg-slate-100 rounded-full" />
                        <div className="h-4 w-12 bg-slate-100 rounded-full" />
                      </div>
                      <div className="h-9 bg-slate-100 rounded-xl" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
                <Car className="w-12 h-12 text-slate-200 mx-auto mb-4" />
                <p className="font-bold text-slate-700 mb-1">
                  {isCity && baseCompanies.length === 0 ? `Nessun noleggio a ${cityName} per ora` : 'Nessun noleggio trovato'}
                </p>
                <p className="text-sm text-slate-400">
                  {isCity && baseCompanies.length === 0
                    ? 'Sfoglia i noleggi delle altre città.'
                    : "Prova a cambiare i filtri o l'area di ricerca"}
                </p>
                {isCity && baseCompanies.length === 0 && (
                  <Link to={copy.parentPath}
                    className="inline-block mt-4 text-xs font-semibold px-4 py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition">
                    Vedi tutte le città
                  </Link>
                )}
                {(query || vehicleFilter !== 'tutti') && (
                  <button type="button" onClick={() => { setQuery(''); setVehicleFilter('tutti'); }}
                    className="mt-4 text-xs font-semibold px-4 py-2 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 transition">
                    Rimuovi filtri
                  </button>
                )}
              </div>
            ) : viewMode === 'list' ? (
              <div className="space-y-3">
                {filtered.map(c => (
                  <CompanyCard key={c.companyId} company={c}
                    isFavorite={!!favorites[c.companyId]} onToggleFavorite={toggleFavorite}
                    dateFrom={dateFrom} dateTo={dateTo} viewMode="list" isLoggedIn={!!user} />
                ))}
              </div>
            ) : (
              <div className={`grid gap-5 ${!showMap ? 'sm:grid-cols-2 lg:grid-cols-3' : ''}`}>
                {filtered.map(c => (
                  <CompanyCard key={c.companyId} company={c}
                    isFavorite={!!favorites[c.companyId]} onToggleFavorite={toggleFavorite}
                    dateFrom={dateFrom} dateTo={dateTo} viewMode="grid" isLoggedIn={!!user} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Testo introduttivo della città (contenuto reale: numero e nomi dei noleggi) */}
        {!loading && isCity && baseCompanies.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <h2 className="text-base font-bold text-slate-700 mb-2">
              {MODE_LABEL[vehicleMode].h1} a {cityName}: {baseCompanies.length === 1 ? 'il noleggio disponibile' : `${baseCompanies.length} noleggi a confronto`}
            </h2>
            <p className="text-sm text-slate-500 leading-relaxed max-w-3xl">
              A {cityName} trovi {baseCompanies.map(c => c.nome).slice(0, 8).join(', ')}
              {baseCompanies.length > 8 ? ` e altri ${baseCompanies.length - 8}` : ''}.
              {' '}Ogni noleggio ha il proprio sito con catalogo, prezzi e disponibilità aggiornati:
              scegli le date, seleziona {MODE_LABEL[vehicleMode].what} e prenota direttamente online.
            </p>
          </div>
        )}

        {/* Elenco città: percorso di scoperta per utenti e motori di ricerca */}
        {!loading && vehicleMode !== 'tutti' && linkableCities.filter(c => c.slug !== cittaParam).length > 0 && (
          <nav className="mt-12 pt-8 border-t border-slate-200" aria-label="Noleggi per città">
            <h2 className="text-base font-bold text-slate-700 mb-3">
              {isCity ? `${MODE_LABEL[vehicleMode].h1} in altre città` : `${MODE_LABEL[vehicleMode].h1} per città`}
            </h2>
            <div className="flex flex-wrap gap-2">
              {linkableCities.filter(c => c.slug !== cittaParam).slice(0, 60).map(c => (
                <Link key={c.slug} to={`${MODE_PATH[vehicleMode]}/${c.slug}`}
                  className="text-sm font-semibold px-3.5 py-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:border-blue-300 hover:text-blue-700 transition">
                  {c.name} <span className="text-slate-400 font-medium">({c.count})</span>
                </Link>
              ))}
            </div>
          </nav>
        )}

        {/* Footer SEO text — aiuta i motori a capire il contesto della pagina */}
        {!loading && filtered.length > 0 && (
          <div className="mt-12 pt-8 border-t border-slate-200">
            <p className="text-xs text-slate-400 leading-relaxed max-w-3xl">
              NoleggioinCloud raccoglie gli autonoleggi e i motonoleggi italiani che usano la piattaforma per gestire prenotazioni online.
              Tutti i noleggi presenti in questa directory accettano prenotazioni online direttamente dal loro sito.
              {vehicleFilter === 'auto' && ' Stai visualizzando solo i noleggi che offrono auto.'}
              {vehicleFilter === 'moto' && ' Stai visualizzando solo i noleggi che offrono moto e scooter.'}
              {addressCoords && ' I risultati sono ordinati per distanza dalla posizione di ricerca.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchCompanies;
